import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Upload,
  PlusCircle,
  Trash2,
  Layers,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  FolderPlus,
} from "lucide-react";

const StudyPlanManager = () => {
  const [durations, setDurations] = useState([]);
  const [plans, setPlans] = useState([]);

  // New Plan Creation
  const [selectedDurationId, setSelectedDurationId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dailyPdfs, setDailyPdfs] = useState([
  { week: "", day: "", date: "", file: null },
]);

const [weeklyPdfs, setWeeklyPdfs] = useState([
  { week: "", file: null },
]);

  // Add PDFs to existing plan
  const [selectedExistingPlanId, setSelectedExistingPlanId] = useState("");
  const [addDailyPdfs, setAddDailyPdfs] = useState([
  { week: "", day: "", date: "", file: null },
]);

const [addWeeklyPdfs, setAddWeeklyPdfs] = useState([
  { week: "", file: null },
]);

  const [expandedPlan, setExpandedPlan] = useState(null);
  const token = JSON.parse(localStorage.getItem("user"))?.token || "";
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    if (token) fetchPlansAndDurations();
  }, [token]);

  const fetchPlansAndDurations = async () => {
    try {
      const [durRes, planRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/study-plan-prices`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/api/admin/study-plans`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setDurations(durRes.data);
      setPlans(planRes.data);
    } catch (err) {
      console.error("Fetch error", err);
    }
  };

  // Helpers
  const handleListChange = (setter, list, idx, field, value) => {
    const updated = [...list];
    updated[idx][field] = field === "file" ? value.target.files[0] : value;
    setter(updated);
  };

  const addEntry = (setter, template) => setter((prev) => [...prev, template]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setStartDate("");
    setSelectedDurationId("");
    setDailyPdfs([{ week: "", day: "", date: "", file: null }]);
    setWeeklyPdfs([{ week: "", file: null }]);
  };

  // Upload new plan
  const handleUpload = async () => {
    if (!selectedDurationId || !title.trim() || !startDate)
      return alert("Please provide title, duration & start date.");

    const selectedDuration = durations.find((d) => d._id === selectedDurationId);
    if (!selectedDuration) return alert("Invalid duration selected.");

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("durationLabel", selectedDuration.durationLabel);
    formData.append("durationInDays", selectedDuration.durationInDays);
    formData.append("price", selectedDuration.price);
    formData.append("startDate", startDate);

    dailyPdfs.forEach((e) => {
      if (e.week && e.day && e.date && e.file) {
        formData.append("dailyPdfs", e.file);
        formData.append("dailyWeeks", e.week);
        formData.append("dailyDays", e.day);
        formData.append("dailyDates", e.date);
      }
    });

    weeklyPdfs.forEach((e) => {
      if (e.week && e.file) {
        formData.append("weeklyPdfs", e.file);
        formData.append("weeklyWeeks", e.week);        
      }
    });

    try {
      await axios.post(`${API_BASE}/api/admin/study-plans`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      alert("✅ Study Plan created successfully!");
      resetForm();
      fetchPlansAndDurations();
    } catch (err) {
      console.error("Upload failed", err);
      alert("❌ Upload failed.");
    }
  };

  const handleAddPdfsUpload = async () => {
    if (!selectedExistingPlanId) return alert("Please select a plan first.");
    const formData = new FormData();

    addDailyPdfs.forEach((e) => {
      if (e.week && e.day && e.date && e.file) {
        formData.append("dailyPdfs", e.file);
        formData.append("dailyWeeks", e.week);
        formData.append("dailyDays", e.day);
        formData.append("dailyDates", e.date);
      }
    });

    addWeeklyPdfs.forEach((e) => {
      if (e.week && e.file) {
        formData.append("weeklyPdfs", e.file);
        formData.append("weeklyWeeks", e.week);        
      }
    });

    try {
      await axios.post(
        `${API_BASE}/api/admin/study-plans/${selectedExistingPlanId}/add-pdfs`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      alert("✅ PDFs added successfully!");
      fetchPlansAndDurations();
    } catch (err) {
      console.error("Add PDFs failed", err);
      alert("❌ Adding PDFs failed.");
    }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/study-plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPlansAndDurations();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const deletePdf = async (planId, pdfId) => {
    if (!window.confirm("Delete this PDF?")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/study-plans/${planId}/pdf/${pdfId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPlansAndDurations();
    } catch (err) {
      console.error("Delete PDF failed", err);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto text-gray-900 dark:text-gray-100">
      <h1 className="text-4xl font-bold text-center mb-10 bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
        📘 Study Plan Manager
      </h1>

      {/* ---------------- CREATE PLAN ---------------- */}
      <section className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl mb-12 border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-semibold flex items-center gap-2 mb-6">
          <Upload className="text-blue-600 w-6 h-6" /> Create New Plan
        </h2>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border p-3 rounded-lg w-full dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Plan Title</label>
            <input
              type="text"
              placeholder="Enter plan title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border p-3 rounded-lg w-full dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Duration</label>
            <select
              value={selectedDurationId}
              onChange={(e) => setSelectedDurationId(e.target.value)}
              className="border p-3 rounded-lg w-full dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="">Select Duration</option>
              {durations.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.durationLabel} — ₹{d.price}
                </option>
              ))}
            </select>
          </div>
        </div>

        <textarea
          placeholder="Enter plan description..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="border p-3 rounded-lg w-full mb-8 dark:bg-gray-700 dark:border-gray-600"
        />

        <div className="grid md:grid-cols-2 gap-8">
          {/* Weekly PDFs */}
          <div className="bg-gray-50 dark:bg-gray-700 p-5 rounded-xl shadow-inner">
            <h3 className="font-semibold text-blue-600 mb-3 flex items-center gap-2">
              <Layers className="w-5 h-5" /> Add Weekly PDFs
            </h3>
            {weeklyPdfs.map((e, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Week"
                  value={e.week}
                  onChange={(ev) =>
                    handleListChange(setWeeklyPdfs, weeklyPdfs, i, "week", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />                
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(ev) => handleListChange(setWeeklyPdfs, weeklyPdfs, i, "file", ev)}
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
              </div>
            ))}
            <button
              onClick={() => addEntry(setWeeklyPdfs, { week: "", file: null })}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2"
            >
              + Add Weekly PDF
            </button>
          </div>

          {/* Daily PDFs */}
          <div className="bg-gray-50 dark:bg-gray-700 p-5 rounded-xl shadow-inner">
            <h3 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
              <CalendarDays className="w-5 h-5" /> Add Daily PDFs
            </h3>
            {dailyPdfs.map((e, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Week"
                  value={e.week}
                  onChange={(ev) =>
                    handleListChange(setDailyPdfs, dailyPdfs, i, "week", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
                <input
                  type="number"
                  placeholder="Day"
                  value={e.day}
                  onChange={(ev) =>
                    handleListChange(setDailyPdfs, dailyPdfs, i, "day", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
                <input
  type="date"
  value={e.date}
  onChange={(ev) =>
    handleListChange(
      setDailyPdfs,
      dailyPdfs,
      i,
      "date",
      ev.target.value
    )
  }
  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
/>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(ev) => handleListChange(setDailyPdfs, dailyPdfs, i, "file", ev)}
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
              </div>
            ))}
            <button
              onClick={() => addEntry(setDailyPdfs, { week: "", day: "", date: "", file: null })}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mt-2"
            >
              + Add Daily PDF
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={handleUpload}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition"
          >
            🚀 Create Study Plan
          </button>
        </div>
      </section>

      {/* ---------------- ADD PDFs TO EXISTING ---------------- */}
      <section className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl mb-12 border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2 text-indigo-500">
          <FolderPlus className="w-6 h-6" /> Add PDFs to Existing Plan
        </h2>

        <select
          value={selectedExistingPlanId}
          onChange={(e) => setSelectedExistingPlanId(e.target.value)}
          className="border p-3 rounded-lg w-full md:w-1/2 mb-6 dark:bg-gray-700 dark:border-gray-600"
        >
          <option value="">Select Existing Plan</option>
          {plans.map((plan) => (
            <option key={plan._id} value={plan._id}>
              {plan.title} ({plan.durationLabel})
            </option>
          ))}
        </select>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Weekly PDFs */}
          <div>
            <h4 className="font-semibold mb-2 text-blue-600">Weekly PDFs</h4>
            {addWeeklyPdfs.map((e, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Week"
                  value={e.week}
                  onChange={(ev) =>
                    handleListChange(setAddWeeklyPdfs, addWeeklyPdfs, i, "week", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />                
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(ev) => handleListChange(setAddWeeklyPdfs, addWeeklyPdfs, i, "file", ev)}
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />                
              </div>
            ))}
            <button
              onClick={() => addEntry(setAddWeeklyPdfs, { week: "", file: null })}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              + Add Weekly PDF
            </button>
          </div>

          {/* Daily PDFs */}
          <div>
            <h4 className="font-semibold mb-2 text-green-600">Daily PDFs</h4>
            {addDailyPdfs.map((e, i) => (
              <div key={i} className="grid grid-cols-4 gap-3 mb-3">
                <input
                  type="number"
                  placeholder="Week"
                  value={e.week}
                  onChange={(ev) =>
                    handleListChange(setAddDailyPdfs, addDailyPdfs, i, "week", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
                <input
                  type="number"
                  placeholder="Day"
                  value={e.day}
                  onChange={(ev) =>
                    handleListChange(setAddDailyPdfs, addDailyPdfs, i, "day", ev.target.value)
                  }
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
                <input
  type="date"
  value={e.date}
  onChange={(ev) =>
    handleListChange(
      setAddDailyPdfs,
      addDailyPdfs,
      i,
      "date",
      ev.target.value
    )
  }
  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
/>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(ev) => handleListChange(setAddDailyPdfs, addDailyPdfs, i, "file", ev)}
                  className="border p-2 rounded dark:bg-gray-600 dark:border-gray-500"
                />
              </div>
            ))}
            <button
              onClick={() => addEntry(setAddDailyPdfs, { week: "", day: "", date: "", file: null })}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              + Add Daily PDF
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={handleAddPdfsUpload}
            className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition"
          >
            ➕ Add PDFs to Plan
          </button>
        </div>
      </section>

      {/* ---------------- UPLOADED PLANS ---------------- */}
      <section className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2 text-green-500">
          <FileText className="w-6 h-6" /> Uploaded Study Plans
        </h2>

        {plans.length === 0 ? (
          <p className="text-gray-500 italic text-center">No plans uploaded yet.</p>
        ) : (
          <div className="grid gap-6">
            {plans.map((plan) => (
              <motion.div
                key={plan._id}
                className="border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 p-5 shadow-md"
              >
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() =>
                    setExpandedPlan(expandedPlan === plan._id ? null : plan._id)
                  }
                >
                  <div>
                    <h4 className="text-lg font-bold">{plan.title}</h4>
                    <p className="text-sm text-gray-400">{plan.durationLabel}</p>
                  </div>
                  {expandedPlan === plan._id ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>

                <AnimatePresence>
                  {expandedPlan === plan._id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 space-y-3"
                    >
                      <p className="text-sm text-gray-300">{plan.description}</p>

                      {/* View PDFs */}
                      {plan.content?.length > 0 && (
                        <>
                          <h5 className="font-semibold mt-2 text-blue-400">Weekly PDFs</h5>
                          {plan.content
                            .filter((pdf) => pdf.week && !pdf.day)
                            .sort((a, b) => Number(a.week) - Number(b.week))
                            .map((pdf) => (
                              <div
                                key={pdf._id}
                                className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-gray-600"
                              >
                                <span>
  Week {pdf.week} — {pdf.title}
</span>
                                <div className="flex gap-3">
                                  <a
                                    href={pdf.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 underline"
                                  >
                                    View
                                  </a>
                                  <button
                                    className="text-red-500"
                                    onClick={() => deletePdf(plan._id, pdf._id)}
                                  >
                                    <Trash2 />
                                  </button>
                                </div>
                              </div>
                            ))}

                          <h5 className="font-semibold mt-4 text-green-400">Daily PDFs</h5>
                          {plan.content
                            .filter((pdf) => pdf.day)
                            .sort(
                              (a, b) =>
                                Number(a.week) - Number(b.week) ||
                                Number(a.day) - Number(b.day)
                            )
                            .map((pdf) => (
                              <div
                                key={pdf._id}
                                className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-gray-600"
                              >
                                <span>
  Week {pdf.week} - Day {pdf.day}
  ({new Date(pdf.scheduledDate).toLocaleDateString("en-IN")})
  — {pdf.title}
</span>
                                <div className="flex gap-3">
                                  <a
                                    href={pdf.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 underline"
                                  >
                                    View
                                  </a>
                                  <button
                                    className="text-red-500"
                                    onClick={() => deletePdf(plan._id, pdf._id)}
                                  >
                                    <Trash2 />
                                  </button>
                                </div>
                              </div>
                            ))}
                        </>
                      )}

                      <button
                        className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                        onClick={() => handleDeletePlan(plan._id)}
                      >
                        🗑️ Delete Entire Plan
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default StudyPlanManager;
